import { Tooltip } from '../../ui/tooltip';
import { IQuoteTemplateFormProps } from '@monetize/types/app';
import {
  ExperienceConfigurationKeys,
  SettingVisibilityEnum,
} from '@monetize/types/experience-config';
import { Controller, useWatch } from 'react-hook-form';
import { useExperienceConfiguration } from '../../../hooks';
import { useFlags } from '../../../services/launchDarkly';
import { MFormField, MFormFieldProps, MSwitch, MText } from '../../Monetize';
import { getVisibleQuoteTemplateSections } from './quoteTemplateSettings.utils';
import { useQuoteTemplateSettingVisibility } from './useQuoteTemplateSettingVisibility';

export const QuoteTemplateForm = ({
  source = 'tenant',
  formState,
  isDisabled,
  requireSigningPage,
  isAmendmentV2Quote,
}: IQuoteTemplateFormProps) => {
  const {
    control,
    formState: { errors },
  } = formState;

  const baseFieldProps: MFormFieldProps = {
    orientation: 'horizontal',
    gap: 4,
    labelProps: { m: 0, fontWeight: 'normal', flex: '1' },
  };

  const fieldProps: MFormFieldProps = {
    ...baseFieldProps,
    py: 3,
    borderBottomWidth: '1px',
    borderBottomColor: 'border.subtle',
  };
  const { hideBillingSchedule } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const flags = useFlags();

  // Role-based visibility for the current user (only enforced for source="quote").
  const visibilityFor = useQuoteTemplateSettingVisibility(source);
  const isHidden = (field: string) =>
    visibilityFor(field) === SettingVisibilityEnum.HIDDEN;
  const isReadOnly = (field: string) =>
    visibilityFor(field) === SettingVisibilityEnum.READ_ONLY;

  // A section header only renders when the user can see at least one of the
  // section's settings.
  const visibleSections = getVisibleQuoteTemplateSections(visibilityFor, {
    isAmendmentV2Quote,
    hideBillingSchedule,
    flags,
  });

  const rateDescriptionField =
    source === 'tenant' ? 'showRateDescription' : 'showOfferingDescription';

  // Discounting Section has no effect while Discounting Details is off, so it
  // shows as off and disabled then. Display only: the stored value is kept.
  const showDiscount = useWatch({ control, name: 'showDiscount' });

  return (
    <>
      {visibleSections.has('includedSections') && (
        <MText fontWeight="bold" mb="2">
          Included Sections in Quote
        </MText>
      )}

      {!isHidden(
        'hideUpdatedContractWhenDeltaViewIsSuccessfulOnAmendments',
      ) && (
        <MFormField
          label="Full Latest Contract"
          {...fieldProps}
          error={
            errors?.hideUpdatedContractWhenDeltaViewIsSuccessfulOnAmendments
          }
        >
          <Controller
            name="hideUpdatedContractWhenDeltaViewIsSuccessfulOnAmendments"
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <MSwitch
                isChecked={!value}
                onChange={(ev) => onChange(!ev.target.checked)}
                {...rest}
                isDisabled={
                  isDisabled ||
                  isReadOnly(
                    'hideUpdatedContractWhenDeltaViewIsSuccessfulOnAmendments',
                  )
                }
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden('showHeaderTotal') && (
        <MFormField
          label="Amount"
          {...fieldProps}
          error={errors?.showHeaderTotal}
          tooltip="Show the total amount at the top of the Quote"
        >
          <Controller
            name="showHeaderTotal"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                data-testid="showHeaderTotal-switch"
                isChecked={value ?? true}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showHeaderTotal')}
              />
            )}
          />
        </MFormField>
      )}

      {flags.enableQuoteYearlyBreakdown && !isHidden('showYearlyBreakDown') && (
        <MFormField
          label="Include Annual Breakdown"
          {...fieldProps}
          error={errors?.showYearlyBreakDown}
        >
          <Controller
            name="showYearlyBreakDown"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                data-testid="showYearlyBreakDown-switch"
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showYearlyBreakDown')}
              />
            )}
          />
        </MFormField>
      )}

      {/* For mercell we are hiding this option completely and it is set to false in DB - no other customers have this flag enabled */}
      {!hideBillingSchedule && (
        <>
          {!isHidden('showBillingSchedule') && (
            <MFormField
              label="Billing Schedule"
              {...fieldProps}
              error={errors?.showBillingSchedule}
            >
              <Controller
                name="showBillingSchedule"
                control={control}
                render={({ field: { value, ...rest } }) => (
                  <MSwitch
                    isChecked={value}
                    {...rest}
                    isDisabled={isDisabled || isReadOnly('showBillingSchedule')}
                  />
                )}
              />
            </MFormField>
          )}
          {!isHidden('showBillingFrequency') && (
            <MFormField
              {...fieldProps}
              label="Billing Frequency"
              error={errors?.showBillingFrequency}
            >
              <Controller
                name="showBillingFrequency"
                control={control}
                render={({ field: { value, ...rest } }) => (
                  <MSwitch
                    isChecked={value}
                    {...rest}
                    isDisabled={
                      isDisabled || isReadOnly('showBillingFrequency')
                    }
                  />
                )}
              />
            </MFormField>
          )}
        </>
      )}

      {!isHidden('showPricing') && (
        <MFormField
          label="Pricing Details"
          {...fieldProps}
          error={errors?.showPricing}
        >
          <Controller
            name="showPricing"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showPricing')}
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden('showDiscount') && (
        <MFormField
          label="Discounting Details"
          {...fieldProps}
          error={errors?.showDiscount}
        >
          <Controller
            name="showDiscount"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                data-testid="showDiscount-switch"
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showDiscount')}
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden('showDiscountSection') && (
        <MFormField
          label="Discounting Section"
          {...fieldProps}
          // Nested under Discounting Details; only indent while that row shows.
          pl={isHidden('showDiscount') ? undefined : 4}
          error={errors?.showDiscountSection}
          tooltip="Show the Discounts summary section on the Quote. Discounting Details must be on for this section to appear."
        >
          <Controller
            name="showDiscountSection"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                data-testid="showDiscountSection-switch"
                isChecked={!!showDiscount && (value ?? true)}
                {...rest}
                isDisabled={
                  isDisabled ||
                  !showDiscount ||
                  isReadOnly('showDiscountSection')
                }
              />
            )}
          />
        </MFormField>
      )}

      {/* Show as true if quote.requiresEsign regardless of actual value */}
      {!isHidden('showSigningPage') && (
        <Tooltip
          content={
            requireSigningPage
              ? 'Signing Sheet will always show because eSign is configured.'
              : undefined
          }
          size="sm"
        >
          <MFormField {...fieldProps} label="Signing Page">
            <Controller
              name="showSigningPage"
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MSwitch
                  isChecked={requireSigningPage ? true : value}
                  {...rest}
                  isDisabled={
                    isDisabled ||
                    requireSigningPage ||
                    isReadOnly('showSigningPage')
                  }
                />
              )}
            />
          </MFormField>
        </Tooltip>
      )}

      {isAmendmentV2Quote && !isHidden('showPriorContractOnAmendments') && (
        <MFormField
          label="Prior Contract (Amendments Only)"
          {...fieldProps}
          error={errors?.showPriorContractOnAmendments}
        >
          <Controller
            name="showPriorContractOnAmendments"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                isChecked={value}
                {...rest}
                isDisabled={
                  isDisabled || isReadOnly('showPriorContractOnAmendments')
                }
              />
            )}
          />
        </MFormField>
      )}

      {isAmendmentV2Quote && !isHidden('showDeltaViewOnAmendments') && (
        <MFormField
          label="Differences from Prior Contract (Amendments Only)"
          {...fieldProps}
          error={errors?.showDeltaViewOnAmendments}
          tooltip="Note: the Full Latest Contract will still show, regardless of this setting, if the Differences From Prior Contract is not available for this Quote"
        >
          <Controller
            name="showDeltaViewOnAmendments"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                isChecked={value}
                {...rest}
                isDisabled={
                  isDisabled || isReadOnly('showDeltaViewOnAmendments')
                }
              />
            )}
          />
        </MFormField>
      )}

      {visibleSections.has('offeringDetails') && (
        <MText fontWeight="bold" mt="4" mb="2">
          Included Details for Offerings
        </MText>
      )}

      {!isHidden('showLineLevelAmount') && (
        <MFormField
          label="Line Amount"
          {...fieldProps}
          error={errors?.showLineLevelAmount}
        >
          <Controller
            name="showLineLevelAmount"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                isChecked={value ?? true}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showLineLevelAmount')}
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden('showRateName') && (
        <MFormField
          {...fieldProps}
          label="Rate Name"
          error={errors?.showRateName}
        >
          <Controller
            name="showRateName"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showRateName')}
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden(rateDescriptionField) && (
        <MFormField {...fieldProps} label="Rate Description">
          <Controller
            name={rateDescriptionField}
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                data-testid={
                  source === 'tenant'
                    ? 'showRateDescription-switch'
                    : 'showOfferingDescription-switch'
                }
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly(rateDescriptionField)}
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden('showProductDescription') && (
        <MFormField
          label="Product Description"
          {...fieldProps}
          error={errors?.showProductDescription}
        >
          <Controller
            name="showProductDescription"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                data-testid="showProductDescription-switch"
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showProductDescription')}
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden('showPricePerUnit') && (
        <MFormField
          label="Price per Unit"
          {...fieldProps}
          error={errors?.showPricePerUnit}
        >
          <Controller
            name="showPricePerUnit"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showPricePerUnit')}
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden('showCredits') && (
        <MFormField
          label="Credits"
          {...fieldProps}
          error={errors?.showCredits}
          tooltip="Show the credits that are created as part of an upgrade, downgrade, removal of an offering, or other actions that create credits"
        >
          <Controller
            name="showCredits"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showCredits')}
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden('showProration') && (
        <MFormField
          label="Proration"
          {...fieldProps}
          error={errors?.showProration}
          tooltip="Show the prorated period for each offering, and scheduled change"
          tooltipSize="sm"
        >
          <Controller
            name="showProration"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showProration')}
              />
            )}
          />
        </MFormField>
      )}

      {!isHidden('showUnusedItems') && (
        <MFormField
          label="Unused Quote Items"
          {...baseFieldProps}
          pt={3}
          error={errors?.showUnusedItems}
          tooltip="Display Quote Items with 0 quantity and 0 amount."
          tooltipSize="xs"
        >
          <Controller
            name="showUnusedItems"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MSwitch
                isChecked={value}
                {...rest}
                isDisabled={isDisabled || isReadOnly('showUnusedItems')}
              />
            )}
          />
        </MFormField>
      )}
    </>
  );
};
